import type { SiteContent } from "@lumora/bridge";
import {
  AuthViewModel,
  bridgeContactSubmit,
  ContactViewModel,
  FeatureRequestViewModel,
  OwnerOrdersViewModel,
  PUBLIC_ORDER_STATUS_ROUTE,
  SiteViewModel,
} from "@lumora/core";
import {
  AboutPage,
  ContactPage,
  FeatureRequestPage,
  OwnerOrdersView,
  SampleWallpaper,
  useViewModel,
  TemplatePublicTicketStatusPage,
  TemplateSignIn,
  TemplateSiteFooter,
} from "@lumora/ui";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { HashRouter, Link, Navigate, Route, Routes, useLocation, useSearchParams } from "react-router-dom";
import { SITE, client } from "./client.js";
import { packageShowsOwnerNav, resolveVariant } from "./variant.js";
import { applyTheme } from "./theme.js";
import { Home } from "./pages/Home.js";
import { PackagesPage } from "./pages/PackagesPage.js";
import { QuotePage } from "./pages/QuotePage.js";
import { SamplePage } from "./pages/SamplePage.js";
import { LegalPage } from "./pages/LegalPage.js";
import { termsDocument } from "./legal/terms.js";
import { privacyDocument } from "./legal/privacy.js";
import { SampleViewModel } from "./viewmodels/SampleViewModel.js";
import { seoFor } from "./seo/pages.js";
import { applyHead } from "./seo/applyHead.js";
import { PackageCatalog } from "./models/PackageCatalog.js";
import { QuoteViewModel } from "./viewmodels/QuoteViewModel.js";

const FOOTER_FALLBACK = {
  tagline: "3D product spins and ad renders for small sellers",
} as SiteContent;

const catalog = PackageCatalog.launch();

/** Reads ?package= so package pages can deep-link into the quote form. */
function QuoteRoute({ submit }: { submit: ConstructorParameters<typeof QuoteViewModel>[0] }) {
  const [params] = useSearchParams();
  const wanted = params.get("package") ?? "";
  const isSample = params.get("type") === "sample";
  const vm = useMemo(
    () => new QuoteViewModel(submit, catalog.has(wanted) ? wanted : ""),
    [submit, wanted],
  );
  const sampleVm = useMemo(() => new SampleViewModel(submit), [submit]);
  return isSample ? <SamplePage viewModel={sampleVm} /> : <QuotePage viewModel={vm} />;
}

/** Applies per-page title/meta/OG/JSON-LD on every route change; moves focus to main for screen readers. */
function RouteEffects() {
  const { pathname } = useLocation();
  useEffect(() => {
    applyHead(seoFor(pathname));
    document.getElementById("main")?.focus({ preventScroll: true });
  }, [pathname]);
  return null;
}

/** Display name; `pnpm new:site --title` rewrites this exact line (never identifiers). */
const SITE_TITLE = "Turnstill";

// Shared Views (About/Contact/SignIn/Orders) reused from @lumora/ui. A site customizes
// only styling (theme tokens) and which sections show (layout) — never the
// functionality. See variant.ts / layout.tsx and .cursor/rules/04-site-theming-and-scaffolding.mdc.
export function App() {
  const variant = useMemo(
    () =>
      resolveVariant({
        search: typeof window !== "undefined" ? window.location.search : "",
        envTheme: import.meta.env.VITE_SITE_THEME,
        envLayout: import.meta.env.VITE_SITE_LAYOUT,
      }),
    [],
  );
  const showOwnerNav = packageShowsOwnerNav();
  const [siteContent, setSiteContent] = useState<SiteContent | null>(null);
  const [signedIn, setSignedIn] = useState(() => Boolean(client.claims()));

  useEffect(() => {
    applyTheme(variant.theme, { layout: variant.layout, colorScheme: "light" });
  }, [variant.theme, variant.layout]);

  const contactSubmit = useMemo(() => bridgeContactSubmit(client, SITE), []);
  const contactVM = useMemo(() => new ContactViewModel(contactSubmit), [contactSubmit]);
  const authVM = useMemo(() => new AuthViewModel(client, SITE), []);
  const featureVM = useMemo(() => new FeatureRequestViewModel(client), []);
  const ordersVM = useMemo(() => new OwnerOrdersViewModel(client), []);
  const siteVM = useMemo(() => new SiteViewModel(client, SITE), []);

  // Closes #574: owner order timestamps follow SiteContent.timezone (not UTC).
  useEffect(() => {
    client
      .getSiteContent(SITE)
      .then((c) => {
        setSiteContent(c);
        ordersVM.setDisplayTimeZone(c.timezone ?? "");
      })
      .catch(() => {
        /* browser-local fallback */
      });
  }, [ordersVM]);

  // Closes #944 / #260: SAMPLE until GET /v1/sites/{slug} reports sample=false (deposit/active).
  useEffect(() => {
    void siteVM.load();
  }, [siteVM]);
  useViewModel(siteVM);

  const footerContent = useMemo<SiteContent>(
    () =>
      ({
        ...FOOTER_FALLBACK,
        ...(siteContent ?? {}),
        tagline: siteContent?.tagline?.trim() || FOOTER_FALLBACK.tagline,
      }) as SiteContent,
    [siteContent],
  );

  // Overlay slot for CartBar when commerce is wired (docs/ui/ordering-menu.md).
  const overlay: ReactNode = null;

  return (
    <HashRouter>
      <RouteEffects />
      <div className="site-shell">
        <a className="skip-link" href="#main" onClick={(e) => { e.preventDefault(); document.getElementById("main")?.focus(); }}>Skip to content</a>
        {siteVM.showsSample() ? <SampleWallpaper /> : null}
        <header className="site-header">
          <Link to="/" className="site-logo">
            {SITE_TITLE}
          </Link>
          <nav aria-label="Main">
            <Link to="/">Home</Link>
            <Link to="/packages">Packages</Link>
            <Link to="/about">About</Link>
            <Link to="/quote" className="site-nav-cta">Get a quote</Link>
            {showOwnerNav ? (
              <>
                <Link to="/orders">Orders</Link>
                <Link to="/signin">Sign in</Link>
              </>
            ) : null}
          </nav>
        </header>
        <main id="main" className="site-main" tabIndex={-1}>
          <Routes>
            <Route path="/" element={<Home layout={variant.layout} />} />
            <Route
              path="/about"
              element={
                <AboutPage
                  className="site-about"
                  title="About Turnstill"
                  paragraphs={[
                    "Turnstill is a small 3D studio that makes product spins, hero stills and ad creatives for sellers on Amazon, TikTok Shop, Etsy and their own stores.",
                    "Everything is built and rendered in Blender on a GPU workstation, so you get consistent, high-resolution images without a photo shoot.",
                    "Property and rental 3D tours are planned as a later service. Use the quote form to join the waitlist.",
                  ]}
                />
              }
            />
            <Route path="/packages" element={<PackagesPage />} />
            <Route path="/quote" element={<QuoteRoute submit={contactSubmit} />} />
            <Route
              path="/contact"
              element={<ContactPage className="site-contact" viewModel={contactVM} />}
            />
            <Route path="/terms" element={<LegalPage doc={termsDocument()} />} />
            <Route path="/privacy" element={<LegalPage doc={privacyDocument()} />} />
            <Route path={PUBLIC_ORDER_STATUS_ROUTE} element={<TemplatePublicTicketStatusPage client={client} slug={SITE} />} />
            {showOwnerNav ? (
              <>
                <Route
                  path="/signin"
                  element={
                    <TemplateSignIn
                      authVM={authVM}
                      onSignedIn={() => setSignedIn(true)}
                    />
                  }
                />
                <Route
                  path="/orders"
                  element={
                    signedIn || client.claims() ? (
                      <OwnerOrdersView viewModel={ordersVM} className="site-orders" />
                    ) : (
                      <Navigate to="/signin" replace />
                    )
                  }
                />
                <Route
                  path="/__/request"
                  element={
                    signedIn || client.claims() ? (
                      <FeatureRequestPage className="site-contact" viewModel={featureVM} />
                    ) : (
                      <Navigate to="/signin" replace />
                    )
                  }
                />
              </>
            ) : null}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>
        {overlay}
        <TemplateSiteFooter siteName={SITE_TITLE} content={footerContent} builtBy />
      </div>
    </HashRouter>
  );
}
